import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton,
  Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import BundleTeaser from './BundleTeaser'
import { copyFor } from './bundleCopy'
import useCanContactVendor from '../hooks/useCanContactVendor'

/**
 * One line saying which checks did not run, and a way to see what they answer.
 *
 * The first version rendered a teaser per refused analyzer inline, two to a
 * row, directly above the findings table. Three things went wrong at once and
 * the user reported all three:
 *
 *   - the cards were huge, so they dominated a page whose subject is findings
 *   - the grid broke the alignment of the severity and category filters below
 *   - **"Illustrative layout — not your estate" sat immediately above the real
 *     findings list, so the findings read as illustrative too**
 *
 * The third is the serious one. That caption exists to stop a blurred skeleton
 * being mistaken for data, and placing it above real rows inverted it into
 * exactly the false impression it was written to prevent — a tenant's genuine
 * findings looking like a mock-up. A sales surface that undermines the product
 * it sits in is worse than no sales surface.
 *
 * So: collapsed to a single line by default, and the teasers open in a dialog.
 * Nothing illustrative shares space with anything real, the filter row is
 * untouched, and the information is still one click away rather than removed —
 * the point was never to hide it, only to stop it shouting over the findings.
 */
interface BundleGapsProps {
  /** Entitlement feature keys refused for want of a bundle, from
   *  `analyzer_coverage`'s `not_entitled` rows. */
  features: string[]
}

const BundleGaps = ({ features }: BundleGapsProps) => {
  const canContactVendor = useCanContactVendor()
  const [open, setOpen] = useState(false)

  // A key with no copy renders nothing rather than an empty frame.
  const entries = features
    .map((f) => ({ feature: f, copy: copyFor(f) }))
    .filter((e): e is { feature: string; copy: NonNullable<ReturnType<typeof copyFor>> } =>
      e.copy !== null)

  if (entries.length === 0) return null

  const bundles = Array.from(new Set(entries.map((e) => e.copy.bundle)))

  return (
    <>
      {/* Severity `info` and one line, so it reads as scan-completeness
          information — which is what it is — rather than as an advert. The
          honest statement comes first; the offer is behind the button. */}
      <Alert
        severity="info"
        variant="outlined"
        sx={{ mb: 2 }}
        action={
          <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>
            What {entries.length === 1 ? 'it shows' : 'these show'}
          </Button>
        }
      >
        <Typography variant="body2">
          {entries.length} {entries.length === 1 ? 'check' : 'checks'} did not run
          on this scan ({bundles.join(' / ')} not enabled), so their absence from
          the findings below is not a clean result.
        </Typography>
      </Alert>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {bundles.join(' and ')}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {entries.map(({ feature, copy }, i) => (
            <Box
              key={feature}
              sx={{
                pb: 2,
                mb: 2,
                borderBottom: i < entries.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <BundleTeaser copy={copy} canContactVendor={canContactVendor} rows={3} compact />
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BundleGaps
